import { type SnackbarKey, useSnackbar } from 'notistack';
import { useCallback } from 'react';

interface UseNotificationReturn {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

function useNotification(): UseNotificationReturn {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const enqueueDismissible = useCallback(
    (message: string, variant: 'success' | 'error' | 'warning' | 'info') => {
      let key: SnackbarKey | undefined;
      key = enqueueSnackbar(message, {
        variant,
        style: { cursor: 'pointer' },
        SnackbarProps: {
          onClick: () => {
            if (key !== undefined) {
              closeSnackbar(key);
            }
          },
        },
      });
    },
    [enqueueSnackbar, closeSnackbar],
  );

  const showSuccess = useCallback(
    (message: string) => enqueueDismissible(message, 'success'),
    [enqueueDismissible],
  );

  const showError = useCallback(
    (message: string) => enqueueDismissible(message, 'error'),
    [enqueueDismissible],
  );

  const showWarning = useCallback(
    (message: string) => enqueueDismissible(message, 'warning'),
    [enqueueDismissible],
  );

  const showInfo = useCallback(
    (message: string) => enqueueDismissible(message, 'info'),
    [enqueueDismissible],
  );

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
}

export { useNotification };
